import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Plus, X } from 'lucide-react'
import { COURSE_LABELS, MEAL_SLOT_LABELS, type Course, type MealSlot } from '@shared/events'
import type { EffectiveDish, RecipePayload } from '@shared/plate'
import { cursoLibre, mostrarCurso, nombreDePlato } from '../lib/platos'

/**
 * Una franja de un día en la semana: su rótulo en la esquina y sus platos en
 * filas, cada uno con su botón de quitar en el mismo sitio.
 *
 * Al cambiar lo que hay, la franja destella en salvia un instante: es la
 * confirmación de que el plato ha caído donde se quería.
 */
export function Franja({
  slot,
  dishes,
  recipes,
  onElegir,
  onQuitar,
}: {
  slot: MealSlot
  dishes: EffectiveDish[]
  recipes: Map<string, RecipePayload>
  /** Con un curso, cambiar ese plato; sin él, añadir uno nuevo. */
  onElegir: (curso: Course | null) => void
  onQuitar: (dish: EffectiveDish) => void
}) {
  const lleno = dishes.length > 0
  const destello = useDestello(dishes.map((d) => `${d.course}:${d.recipeId}:${d.freeText}`).join('|'))
  const conCurso = mostrarCurso(dishes)
  const libre = cursoLibre(dishes.map((dish) => dish.course))

  return (
    <div className="relative pt-2">
      <span
        className={`label-caps pointer-events-none absolute left-3 top-2 z-10 -translate-y-1/2 rounded-full border bg-card px-2 py-[3px] ${
          lleno ? 'border-sage/50 text-sage-deep' : 'border-border text-muted-foreground'
        }`}
      >
        {MEAL_SLOT_LABELS[slot]}
      </span>

      {!lleno ? (
        <motion.button
          type="button"
          onClick={() => onElegir(null)}
          whileTap={{ scale: 0.985 }}
          aria-label={`Añadir ${MEAL_SLOT_LABELS[slot].toLowerCase()}`}
          className="flex min-h-12 w-full items-center gap-1.5 rounded-2xl border border-dashed border-border px-3 pb-3 pt-4 text-left text-sm text-muted-foreground outline-none transition-colors hover:border-primary/50 hover:bg-accent/60 focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Plus className="h-4 w-4 shrink-0" strokeWidth={2.4} />
          añadir plato
        </motion.button>
      ) : (
        <div
          className={`rounded-2xl border border-sage/40 bg-filled px-1 pb-1 pt-3 ${destello ? 'animate-settle' : ''}`}
        >
          <ul>
            <AnimatePresence initial={false}>
              {dishes.map((dish) => (
                <motion.li
                  key={dish.course}
                  layout
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.22, ease: 'easeOut' }}
                  className="flex items-center"
                >
                  <button
                    type="button"
                    onClick={() => onElegir(dish.course)}
                    className="flex min-h-11 min-w-0 flex-1 items-baseline gap-2 rounded-xl px-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {conCurso && (
                      <span className="w-[4.6rem] shrink-0 text-[0.7rem] font-medium uppercase tracking-wider text-muted-foreground">
                        {COURSE_LABELS[dish.course]}
                      </span>
                    )}
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.span
                        key={`${dish.recipeId}:${dish.freeText}`}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 8 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="min-w-0 truncate text-sm font-medium"
                      >
                        {nombreDePlato(dish, recipes)}
                      </motion.span>
                    </AnimatePresence>
                  </button>
                  <button
                    type="button"
                    onClick={() => onQuitar(dish)}
                    aria-label={`Quitar ${nombreDePlato(dish, recipes)} de ${MEAL_SLOT_LABELS[slot].toLowerCase()}`}
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-muted-foreground outline-none transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <X className="h-4 w-4" strokeWidth={2.4} />
                  </button>
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
          {libre && (
            <button
              type="button"
              onClick={() => onElegir(null)}
              className="ml-1 inline-flex min-h-9 items-center gap-1 rounded-full px-2 text-xs font-medium text-sage-deep outline-none transition-colors hover:bg-card/70 focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Plus className="h-3.5 w-3.5" strokeWidth={2.4} />
              otro plato
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/** Verdadero durante un instante cada vez que cambia la firma, salvo al montar. */
function useDestello(firma: string): boolean {
  const anterior = useRef(firma)
  const [activo, setActivo] = useState(false)

  useEffect(() => {
    if (anterior.current === firma) return
    const habiaAlgo = anterior.current !== ''
    anterior.current = firma
    if (!firma && habiaAlgo) return
    setActivo(true)
    const timer = window.setTimeout(() => setActivo(false), 650)
    return () => window.clearTimeout(timer)
  }, [firma])

  return activo
}
